import { useMemo, useState } from 'react'
import {
  analyze,
  blockingReasons,
  validateGroups,
  verdictOf,
  type AnalysisSettings,
  type GroupInput,
  type SequentialContext,
  type VariantResult,
} from '../lib/stats/analysis'
import {
  formatAlpha,
  formatConfidence,
  formatCount,
  VERDICT_LABELS,
  formatInterval,
  formatLift,
  formatPValue,
  formatPercent,
  formatPoints,
} from '../lib/format'
import {
  buildReport,
  REPORT_FORMAT_LABELS,
  type ReportFormat,
} from '../lib/report'
import { defaultGroupName, encodeState } from '../lib/urlState'
import { IssueList } from './IssueList'
import { ResultGuard } from './ResultGuard'
import { NumberField } from './NumberField'
import { InfoTip } from './InfoTip'
import { SequentialCard, TestSettingsCard } from './TestSettings'
import { IntervalChart, type ChartRow, type ChartTone } from './IntervalChart'

interface Props {
  groups: GroupInput[]
  onGroupsChange: (groups: GroupInput[]) => void
  settings: AnalysisSettings
  onSettingsChange: (settings: AnalysisSettings) => void
}

const METRIC_HINTS = {
  cvr:
    'この群のコンバージョン率です。成果数 ÷ 訪問者数 で求めます。' +
    '下の小さい数字は対照群の CVR です。',
  absolute:
    '比較群の CVR から対照群の CVR を引いた差です。単位はパーセントポイント (pt) で、' +
    '5% から 6% への変化は「1pt の差」です。下の括弧は信頼区間で、' +
    'これが 0pt をまたいでいる間は差があるとは言えません。',
  relative:
    '対照群を基準にした増減率です（絶対差 ÷ 対照群の CVR）。同じ 1pt の差でも、' +
    '元の CVR が低いほど相対リフトは大きく出ます。比の分布は右に歪むため、' +
    '区間は対数変換して求めています。',
  pValue:
    '本当は差がないのに、観測された差以上のものが偶然出てしまう確率です。' +
    '有意水準（1 − 信頼水準）を下回ると有意と判定します。差の大きさを表す値ではないので、' +
    '信頼区間とあわせて見てください。p 値はプールした標準誤差、信頼区間はプールしない標準誤差を' +
    '使うため、境界付近では判定が食い違うことがあります。',
  adjustedPValue:
    '多重比較補正をかけたあとの p 値です。比較の回数が増えるほど偶然の有意が出やすくなるため、' +
    'その分だけ厳しく評価しています。判定にはこの値を使い、下に補正前の値を併記しています。',
} as const

export function SignificanceView({
  groups,
  onGroupsChange,
  settings,
  onSettingsChange,
}: Props) {
  const [shareStatus, setShareStatus] = useState<string | null>(null)
  // クリップボードが使えない環境で、本文を手で選択できるようにするための控え
  const [fallbackText, setFallbackText] = useState<string | null>(null)
  // 均等割付でない設計は少数派なので、既定では列を隠しておく。
  // 共有 URL に不均等な割付比が載っていた場合だけ開いた状態で始める。
  const [showAllocation, setShowAllocation] = useState(() =>
    groups.some((g) => (g.weight ?? 1) !== 1),
  )

  const validationErrors = useMemo(() => validateGroups(groups), [groups])
  const analysis = useMemo(
    () => (validationErrors.length === 0 ? analyze(groups, settings) : null),
    [groups, settings, validationErrors],
  )

  const errorFor = (
    groupId: string,
    field: 'visitors' | 'conversions' | 'weight',
  ) =>
    validationErrors.find((e) => e.groupId === groupId && e.field === field)
      ?.message

  const updateGroup = (id: string, patch: Partial<GroupInput>) => {
    onGroupsChange(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)))
  }

  const addGroup = () => {
    const index = groups.length
    onGroupsChange([
      ...groups,
      {
        id: `g${Date.now()}`,
        name: defaultGroupName(index),
        visitors: 1000,
        conversions: 100,
        weight: 1,
      },
    ])
  }

  const removeGroup = (id: string) => {
    onGroupsChange(groups.filter((g) => g.id !== id))
  }

  /** 割付比の指定をやめたら、表示と分析がずれないよう均等割付に戻す。 */
  const toggleAllocation = () => {
    if (showAllocation) {
      onGroupsChange(groups.map((g) => ({ ...g, weight: 1 })))
    }
    setShowAllocation(!showAllocation)
  }

  const shareQuery = () =>
    encodeState({
      groups: groups.map((g) => ({
        name: g.name,
        visitors: g.visitors,
        conversions: g.conversions,
        weight: g.weight ?? 1,
      })),
      confidence: settings.confidence,
      tail: settings.tail,
      correction: settings.correction,
      sequential: settings.sequential,
    })

  const notify = (message: string) => {
    setShareStatus(message)
    window.setTimeout(() => setShareStatus(null), 4000)
  }

  const share = async () => {
    const query = shareQuery()
    const url = `${window.location.origin}${window.location.pathname}?${query}`
    try {
      await navigator.clipboard.writeText(url)
      setFallbackText(null)
      notify('リンクをコピーしました')
    } catch {
      // クリップボード API は権限や非セキュアな文脈で失敗する。
      // その場合でも URL 自体は残しておく。
      window.history.replaceState(null, '', `?${query}`)
      notify('URL を更新しました（アドレスバーからコピーしてください）')
    }
  }

  const copyReport = async (format: ReportFormat) => {
    if (!analysis) return
    const query = shareQuery()
    const text = buildReport({
      analysis,
      settings,
      format,
      url: `${window.location.origin}${window.location.pathname}?${query}`,
    })
    try {
      await navigator.clipboard.writeText(text)
      setFallbackText(null)
      notify(`${REPORT_FORMAT_LABELS[format]} 形式でコピーしました`)
    } catch {
      // コピーできない環境でも本文は渡す。ここで諦めると
      // 画面から手で書き写すことになり、警告が落ちた要約が出回る。
      setFallbackText(text)
      notify('コピーできませんでした。下の本文を選択してください')
    }
  }

  return (
    <>
      <section className="card">
        <h2 className="card-title">観測データ</h2>
        <p className="card-hint">
          先頭の群が対照群です。訪問者数は「テストの対象になった人数」、成果数はそのうち
          コンバージョンした人数を入れてください。
        </p>

        <div className={`groups ${showAllocation ? 'groups-with-weight' : ''}`}>
          <div className="groups-head-row" aria-hidden="true">
            <div className="groups-head">群の名前</div>
            {showAllocation && (
              <div className="groups-head groups-head-numeric">割付比</div>
            )}
            <div className="groups-head groups-head-numeric">訪問者数</div>
            <div className="groups-head groups-head-numeric">成果数</div>
            <div className="groups-head groups-head-numeric">CVR</div>
            <div className="groups-head" />
          </div>

          {groups.map((group, index) => (
            <GroupRow
              key={group.id}
              group={group}
              isControl={index === 0}
              canRemove={groups.length > 2}
              showAllocation={showAllocation}
              visitorsError={errorFor(group.id, 'visitors')}
              conversionsError={errorFor(group.id, 'conversions')}
              weightError={errorFor(group.id, 'weight')}
              onChange={(patch) => updateGroup(group.id, patch)}
              onRemove={() => removeGroup(group.id)}
            />
          ))}
        </div>

        <div className="groups-actions">
          <button type="button" className="button" onClick={addGroup}>
            + 比較群を追加
          </button>
          <button
            type="button"
            className="button"
            aria-pressed={showAllocation}
            onClick={toggleAllocation}
          >
            割付比を指定
          </button>
        </div>

        {showAllocation && (
          <p className="card-hint" style={{ margin: '10px 0 0' }}>
            設計上の振り分け比率です（80:20 なら 80 と 20、または 4 と 1）。
            SRM の判定はこの比率を期待値として行うため、均等割付でない実験を
            均等前提で見て誤警告が出るのを防げます。実際の人数ではなく
            <strong>設計値</strong>を入れてください。
          </p>
        )}
      </section>

      <TestSettingsCard
        settings={settings}
        onSettingsChange={onSettingsChange}
      />

      <SequentialCard
        settings={settings}
        onSettingsChange={onSettingsChange}
        sequential={analysis?.sequential ?? null}
      />

      {validationErrors.length > 0 && (
        <IssueList
          issues={[
            {
              level: 'error',
              title: '入力を確認してください',
              detail: '赤くなっている欄を直すと結果が表示されます。',
            },
          ]}
        />
      )}

      {analysis && (
        <>
          <IssueList issues={analysis.issues} />

          <ResultGuard
            blocked={analysis.resultsBlocked}
            reasons={blockingReasons(analysis.issues)}
          >
            <h2 className="section-title">判定</h2>
            {analysis.variants.map((variant) => (
              <VerdictCard
                key={variant.group.id}
                variant={variant}
                controlName={analysis.control.group.name}
                settings={settings}
                sequential={analysis.sequential}
                correctionApplied={
                  settings.correction !== 'none' && analysis.comparisonCount > 1
                }
              />
            ))}

            <section className="card">
              <h2 className="card-title">各群のコンバージョン率と信頼区間</h2>
              <p className="card-hint">
                点が観測された CVR、横棒が
                {formatConfidence(1 - analysis.intervalAlpha)}
                信頼区間（Wilson スコア区間）です。
              </p>
              <IntervalChart
                caption="各群のコンバージョン率と信頼区間"
                rows={rateRows(analysis)}
                formatTick={(v) => formatPercent(v, 1)}
              />
            </section>

            <section className="card">
              <h2 className="card-title">対照群との差と信頼区間</h2>
              <p className="card-hint">
                横棒が 0 の破線をまたいでいる間は「差があるとは言えない」状態です。
                棒全体が片側に寄って初めて差の向きを主張できます。
              </p>
              <IntervalChart
                caption="対照群との差と信頼区間"
                rows={diffRows(analysis)}
                formatTick={(v) => `${(v * 100).toFixed(1)}pt`}
                showZeroLine
              />
            </section>
          </ResultGuard>

          <section className="card">
            <h2 className="card-title">結果の共有</h2>
            <div className="share-row">
              <button type="button" className="button" onClick={share}>
                この結果のリンクをコピー
              </button>
              {(['markdown', 'slack'] as ReportFormat[]).map((format) => (
                <button
                  key={format}
                  type="button"
                  className="button"
                  onClick={() => copyReport(format)}
                >
                  {REPORT_FORMAT_LABELS[format]} でコピー
                </button>
              ))}
              {shareStatus && <span className="share-status">{shareStatus}</span>}
            </div>
            <p className="card-hint" style={{ margin: '10px 0 0' }}>
              リンクには入力値と設定が入るので、同じ画面をそのまま再現できます。
              本文でのコピーには、判定の条件と品質の警告も必ず含まれます
              （都合のよい数字だけを転記した要約が出回らないようにするためです）。
              計算はすべてブラウザ内で行われ、数値がサーバーに送られることはありません。
            </p>
            {fallbackText && (
              <textarea
                className="share-fallback"
                readOnly
                rows={12}
                aria-label="コピーする本文"
                value={fallbackText}
              />
            )}
          </section>
        </>
      )}
    </>
  )
}

interface GroupRowProps {
  group: GroupInput
  isControl: boolean
  canRemove: boolean
  showAllocation: boolean
  visitorsError: string | undefined
  conversionsError: string | undefined
  weightError: string | undefined
  onChange: (patch: Partial<GroupInput>) => void
  onRemove: () => void
}

function GroupRow({
  group,
  isControl,
  canRemove,
  showAllocation,
  visitorsError,
  conversionsError,
  weightError,
  onChange,
  onRemove,
}: GroupRowProps) {
  const rate =
    Number.isFinite(group.visitors) &&
    Number.isFinite(group.conversions) &&
    group.visitors > 0
      ? group.conversions / group.visitors
      : NaN

  return (
    <div className="group-row">
      <div className="group-cell group-cell-name">
        <span
          className={`group-badge ${isControl ? 'group-badge-control' : ''}`}
        >
          {isControl ? '対照群' : '比較群'}
        </span>
        <input
          type="text"
          value={group.name}
          aria-label="群の名前"
          onChange={(event) => onChange({ name: event.target.value })}
        />
      </div>
      {showAllocation && (
        <div className="group-cell">
          <span className="group-cell-label">割付比</span>
          <NumberField
            ariaLabel={`${group.name} の割付比`}
            value={group.weight ?? 1}
            min={0}
            step={1}
            error={weightError}
            onChange={(weight) => onChange({ weight })}
          />
        </div>
      )}
      <div className="group-cell">
        <span className="group-cell-label">訪問者数</span>
        <NumberField
          ariaLabel={`${group.name} の訪問者数`}
          value={group.visitors}
          min={1}
          step={1}
          error={visitorsError}
          onChange={(visitors) => onChange({ visitors })}
        />
      </div>
      <div className="group-cell">
        <span className="group-cell-label">成果数</span>
        <NumberField
          ariaLabel={`${group.name} の成果数`}
          value={group.conversions}
          min={0}
          step={1}
          error={conversionsError}
          onChange={(conversions) => onChange({ conversions })}
        />
      </div>
      <div className="group-cell group-cell-rate">
        <span className="group-cell-label">CVR</span>
        <span className="group-rate">{formatPercent(rate)}</span>
      </div>
      <div className="group-cell group-cell-action">
        {canRemove && !isControl && (
          <button
            type="button"
            className="button button-ghost"
            onClick={onRemove}
            aria-label={`${group.name} を削除`}
            title="この群を削除"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}

interface VerdictCardProps {
  variant: VariantResult
  controlName: string
  settings: AnalysisSettings
  sequential: SequentialContext | null
  correctionApplied: boolean
}

function VerdictCard({
  variant,
  controlName,
  settings,
  sequential,
  correctionApplied,
}: VerdictCardProps) {
  const { comparison, significant, mde } = variant
  const improved = comparison.absoluteDiff > 0
  const verdict = verdictOf(variant)
  const tone =
    verdict === 'improved'
      ? 'significant'
      : verdict === 'worsened'
        ? 'negative'
        : 'inconclusive'
  const label = VERDICT_LABELS[verdict]

  const confidenceLabel = formatPercent(settings.confidence, 0)

  return (
    <div className={`verdict verdict-${tone}`}>
      <div className="verdict-head">
        <span className="verdict-name">
          {variant.group.name} vs {controlName}
        </span>
        <span className="verdict-label">{label}</span>
        <span className="verdict-summary">
          {sequential
            ? // 判定の基準が名目の水準ではないので、どの基準で判断したかを書く
              `${sequential.settings.currentLook} 回目の確認の基準 (p < ` +
              `${formatAlpha(sequential.current.nominalAlpha)}) を` +
              `${significant ? '満たします' : '満たしません'}`
            : significant
              ? `${confidenceLabel} 信頼水準で、差があると言えます`
              : `${confidenceLabel} 信頼水準では、差があるとは言えません`}
        </span>
      </div>

      <div className="metrics">
        <Metric
          label="CVR"
          hint={METRIC_HINTS.cvr}
          value={formatPercent(comparison.variant.rate)}
          sub={`対照群 ${formatPercent(comparison.control.rate)}`}
        />
        <Metric
          label="絶対差"
          hint={METRIC_HINTS.absolute}
          value={formatPoints(comparison.absoluteDiff)}
          sub={formatInterval(comparison.absoluteDiffInterval, (v) =>
            formatPoints(v),
          )}
          tone={improved ? 'positive' : 'negative'}
        />
        <Metric
          label="相対リフト"
          hint={METRIC_HINTS.relative}
          value={formatLift(comparison.relativeLift)}
          sub={formatInterval(comparison.relativeLiftInterval, (v) =>
            formatLift(v),
          )}
          tone={improved ? 'positive' : 'negative'}
        />
        <Metric
          label={correctionApplied ? '補正後 p 値' : 'p 値'}
          hint={
            correctionApplied
              ? METRIC_HINTS.adjustedPValue
              : METRIC_HINTS.pValue
          }
          value={formatPValue(
            correctionApplied ? variant.adjustedPValue : variant.rawPValue,
          )}
          sub={
            correctionApplied
              ? `補正前 ${formatPValue(variant.rawPValue)}`
              : comparison.z !== null
                ? `z = ${comparison.z.toFixed(3)}`
                : '計算不能'
          }
        />
      </div>

      {!significant && (
        <div className="verdict-note">
          {mde ? (
            <>
              この人数（{formatCount(comparison.control.n)} 人 /{' '}
              {formatCount(comparison.variant.n)} 人）で検出力{' '}
              {formatPercent(settings.diagnosticPower, 0)} を確保できるのは、
              大きさが{' '}
              <strong>{formatPoints(Math.abs(mde.absolute))} 以上</strong>
              （相対{' '}
              <strong>{formatLift(Math.abs(mde.relative))} 以上</strong>）
              の差までです。観測された差は{' '}
              {formatPoints(comparison.absoluteDiff)} なので、
              {Math.abs(comparison.absoluteDiff) < Math.abs(mde.absolute)
                ? '「差がない」ではなく「この人数では判断できない」状態です。'
                : '効果量は足りていますが、ばらつきの範囲に収まっています。'}
            </>
          ) : (
            <>
              この人数では、検出力{' '}
              {formatPercent(settings.diagnosticPower, 0)} を確保できる効果量が
              存在しません。サンプルを増やす必要があります。
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Metric({
  label,
  hint,
  value,
  sub,
  tone,
}: {
  label: string
  hint?: string
  value: string
  sub?: string
  tone?: 'positive' | 'negative'
}) {
  return (
    <div className="metric">
      <div className="metric-label">
        {label}
        {hint && <InfoTip label={label} text={hint} />}
      </div>
      <div className={`metric-value ${tone ? `metric-${tone}` : ''}`}>{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  )
}

function toneFor(variant: VariantResult): ChartTone {
  const verdict = verdictOf(variant)
  if (verdict === 'inconclusive') return 'variant'
  return verdict === 'improved' ? 'significant' : 'negative'
}

function rateRows(analysis: NonNullable<ReturnType<typeof analyze>>): ChartRow[] {
  return [
    {
      label: analysis.control.group.name,
      point: analysis.control.estimate.rate,
      low: analysis.control.estimate.interval.low,
      high: analysis.control.estimate.interval.high,
      tone: 'control' as ChartTone,
    },
    ...analysis.variants.map((variant) => ({
      label: variant.group.name,
      point: variant.comparison.variant.rate,
      low: variant.comparison.variant.interval.low,
      high: variant.comparison.variant.interval.high,
      tone: toneFor(variant),
    })),
  ]
}

function diffRows(analysis: NonNullable<ReturnType<typeof analyze>>): ChartRow[] {
  return analysis.variants.map((variant) => ({
    label: variant.group.name,
    point: variant.comparison.absoluteDiff,
    low: variant.comparison.absoluteDiffInterval.low,
    high: variant.comparison.absoluteDiffInterval.high,
    tone: toneFor(variant),
  }))
}
